/* eslint-disable no-unused-vars */
import { Button, Input, Spin, Tag } from "antd";
import axios from "axios";
import moment from "moment/moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { FiEdit, FiEye, FiTrash } from "react-icons/fi";
import { useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import DataTable from "@/components/DataTable/DataTable";
import { userRole } from "@/constant";
import { flare } from "@flareapp/js";

const AlarmSummary = () => {
  const userDetails = useSelector((state) => state.user?.userDetails);
  const [searchParams] = useSearchParams();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [filter, setFilter] = useState({
    site: "",
    from: searchParams.get("startDate") ? searchParams.get("startDate") : "",
    to: searchParams.get("endDate") ? searchParams.get("endDate") : "",
  });
  const { from, to } = filter;

  const getNotifications = async () => {
    setIsLoading(true);
    try {
      let { results, count } = await api.get(endpoint.notification, {
        params: { limit, page, sortBy: "createdAt", orderBy: "DESC", from, to, searchText, unknown: true },
        withCredentials: true,
      });

      setNotifications(results);
      setTotal(count || 0);
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("get notifications", error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    document.title = `${appConfig.name} - System Alarm Summary`;
    getNotifications();
  }, [page, limit, searchText]);

  const columns = [
    {
      title: "No.",
      render: (row, _, index) => (page - 1) * limit + index + 1,
      width: "60px",
      align: "center",
    },
    {
      title: "Alarm Name",
      render: (row) => <div>{row?.name}</div>,
    },
    {
      title: "Device Name",
      render: (row) => (
        <div>
          <LimitedTags list={row?.device} />
        </div>
      ),
      width: "150px",
    },
    {
      title: "Device Type",
      render: (row) => (
        <div>
          <LimitedTags list={row?.device?.map((item) => item?.deviceType)} />
        </div>
      ),
    },
    {
      title: "Site Name",
      render: (row) => (
        <div>
          <LimitedTags list={row?.site} />
        </div>
      ),
    },
    {
      title: "Created By",
      render: (row) => row?.createdBy?.name,
    },
    {
      title: "Date Created",
      render: (row) => moment(row?.createdAt).format("DD/MM/YYYY"),
    },
    {
      title: "Action",
      render: (row) => (
        <div>
          <Link to={`/alarm-view/` + row?._id} title="View Alarm" className="btn btn-az-primary me-1">
            <FiEye />
          </Link>
          {userDetails?.role !== userRole.public ? (
            <>
              <Link to={`/edit-alarm/` + row?._id} className="btn btn-edit me-1">
                <FiEdit title="Edit" />
              </Link>
              <button className="btn btn-danger" onClick={() => deleteAlarm(row?._id)}>
                <FiTrash title="Delete" />
              </button>
            </>
          ) : (
            ""
          )}
        </div>
      ),
      width: "220px",
      align: "center",
    },
  ];

  const LimitedTags = ({ list, limit = 3 }) => (
    <>
      {Array.isArray(list)
        ? list?.map((item, idx) =>
            idx < limit ? (
              <Tag key={idx} className="d-block my-1">
                {" "}
                {item?.name}{" "}
              </Tag>
            ) : (
              ""
            ),
          )
        : ""}
      {list?.length > limit ? <Tag className="d-inline-block my-1"> + {list?.length - limit} ... </Tag> : ""}
    </>
  );

  LimitedTags.propTypes = {
    list: PropTypes.array,
    limit: PropTypes.number,
  };

  const deleteAlarm = async (alarmId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this Alarm?",
      //icon: "warning",
      dangerMode: true,
      showCancelButton: true,
      confirmButtonText: "Confirm",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`/notification/` + alarmId, {
            withCredentials: true,
          })
          .then((res) => {
            getNotifications();
            Swal.fire({
              title: "Done!",
              text: "Alarm Deleted Successfully",
              icon: "success",
              timer: 2000,
              button: false,
            });
          });
      }
    });
  };

  return (
    <div className="settings">
      <div className="card p-3 m-md-3">
        <div className="d-flex flex-column flex-md-row g-3 align-items-md-center mb-3 justify-content-between">
          <div>
            <label className="az-content-label">Manage Alarm</label>
            <span className="d-block py-2">System Alarm Summary</span>
          </div>
          <div className="ms-md-auto p-0">
            {userDetails?.role !== userRole.public && (
              <Link to="/create-alarm">
                <Button icon={<FaPlus />} type="primary">
                  Create New Alarm
                </Button>
              </Link>
            )}
          </div>
        </div>
        <hr className="mt-0" />
        {/* <h3>System Alarm Summary</h3> */}
        <div className="row">
          <div className="col-md-10"></div>
          <div className="col-md-2">
            <Input
              type="search"
              size="large"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(1);
              }}
              placeholder="Search"
              // className="me-2"
              aria-label="Search"
            />
          </div>
        </div>
        <Spin spinning={isLoading}>
          <DataTable
            columns={columns}
            dataSource={notifications}
            pagination={{
              pageSize: limit,
              current: page,
              total,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              pageSizeOptions: [10, 20, 50],
              responsive: true,
            }}
            onChange={({ pageSize, current }) => {
              setLimit(pageSize);
              setPage(current);
            }}
          />
        </Spin>
      </div>
    </div>
  );
};

export default AlarmSummary;
